import { rootStore } from "stores/root-store"
import AutocompleteEditInputCell from "components/common/autocomplete-edit-input-cell"
import { GridEditInputCell } from "@mui/x-data-grid"
import { deliveryCodes, ausStates, exceptionTypeLabels } from "shared/constants/sale-order-constant"
import { formatDisplayDateTime, parseDate, formatDisplayDate } from "utils/date-helper"
import RadioGroupEditInputCell from "components/common/radiogroup-edit-input-cell"
import StatusComponent from "components/common/status-component"
import { GlobalData } from "shared/global-data"
import DateTimeEditInputCell from "components/common/datetime-edit-input-cell"
import { CommonHyperlink } from "components/common-styles"

export const filterData = [
  {
    name: "before_date",
    text: "Before Date",
    checked: true,
  },
  {
    name: "after_date",
    text: "After Date",
    checked: true,
  },
  {
    name: "order_status",
    text: "Order Status",
  },
  {
    name: "exception_type",
    text: "Exception Type",
  },
  {
    name: "warehouse",
    text: "Warehouse",
  },
  {
    name: "carrier",
    text: "Carrier",
  },
  {
    name: "country",
    text: "Country",
  },
  {
    name: "sales_channel",
    text: "Sales Channel",
  },
  /*{
    name: "sku",
    text: "SKU Codes",
  },*/
]

export const hiddenColumns = {
  SHIP_TO_ADDRESS1: false,
  SHIP_TO_ADDRESS2: false,
  SHIP_TO_CITY: false,
  SHIP_TO_POSTAL_CODE: false,
  SHIP_TO_EMAIL_ADDRESS: false,
  SHIP_TO_PHONE_NUM: false,
  DELIVERY_CODE: false,
  SALES_CHANNEL: false,
  BRAND_CODE: false,
  CREATION_DATETIME: false,
  INCOTERMS: false,
  LAST_UPDATE_DATETIME: false,
  SCHEDULED_SHIP_DATE: false,
  EXCEPTION_TYPE: false,
}

export const columns = [
  {
    field: "ERP_ORDER",
    headerName: "Order Number",
    flex: 1,
    minWidth: 200,
    renderCell: params => <CommonHyperlink to={`/sales-order/${encodeURIComponent(params.value)}/${encodeURIComponent(params.row["ID"])}`}>{params.value}</CommonHyperlink>,
  },
  {
    field: "WAREHOUSE",
    headerName: "Warehouse",
  },
  {
    field: "SHIP_TO",
    headerName: "Company",
    width: 150,
  },
  {
    field: "SHIP_TO_NAME",
    headerName: "Name",
    minWidth: 150,
    flex: 1,
  },
  {
    field: "SHIP_TO_ADDRESS1",
    headerName: "Address Line 1",
    width: 200,
  },
  {
    field: "SHIP_TO_ADDRESS2",
    headerName: "Address Line 2",
    width: 200,
  },
  {
    field: "SHIP_TO_CITY",
    headerName: "Suburb",
    width: 150,
  },
  {
    field: "SHIP_TO_STATE",
    headerName: "State",
  },
  {
    field: "SHIP_TO_COUNTRY",
    headerName: "Country",
  },
  {
    field: "SHIP_TO_POSTAL_CODE",
    headerName: "Postcode",
  },
  {
    field: "SHIP_TO_EMAIL_ADDRESS",
    headerName: "Email",
    width: 150,
  },
  {
    field: "SHIP_TO_PHONE_NUM",
    headerName: "Phone",
    width: 150,
  },
  {
    field: "SHIP_TO_ATTENTION_TO",
    headerName: "Attention To",
    width: 150,
  },
  {
    field: "ORDER_STATUS",
    headerName: "Order Status",
    width: 150,
    renderCell: params => <StatusComponent status={params.value} />,
  },
  {
    field: "EXCEPTION_TYPE",
    headerName: "Exception Type",
    minWidth: 180,
    renderCell: params => exceptionTypeLabels[params.value] || params.value,
  },
  {
    field: "DELIVERY_CODE",
    headerName: "Delivery Code",
  },
  {
    field: "CARRIER",
    headerName: "Carrier",
    width: 120,
  },
  {
    field: "CARRIER_SERVICE",
    headerName: "Carrier Service",
    width: 150,
  },
  {
    field: "SALES_CHANNEL",
    headerName: "Sales Channel",
    width: 150,
  },
  {
    field: "BRAND_CODE",
    headerName: "Brand Code",
    width: 150,
  },
  {
    field: "CREATION_DATETIME",
    headerName: "Creation Date",
    width: 150,
    renderCell: params => {
      return formatDisplayDateTime(params.value)
    },
  },
  {
    field: "TRACKING_NUM",
    headerName: "Tracking Number",
    width: 150,
  },
  {
    field: "INCOTERMS",
    headerName: "Incoterm",
  },
  {
    field: "LAST_UPDATE_DATETIME",
    headerName: "Closed Date",
    width: 150,
    renderCell: params => {
      if (params.row?.ORDER_STATUS != "Closed") return ""

      return formatDisplayDateTime(params.value)
    },
  },
  {
    field: "SCHEDULED_SHIP_DATE",
    headerName: "Planned Ship Date",
    width: 150,
    renderCell: params => {
      return formatDisplayDate(params.value)
    },
  },
]

export const columnsUploadFunctionsMap = {
  ship_to_state: {
    renderEditCell: params => {
      if (params.row.ship_to_country == "AU") return <AutocompleteEditInputCell {...params} options={ausStates.map(option => ({ label: option, id: option }))} />

      return <GridEditInputCell {...params} />
    },
    customValidation: (rowId, val, allData) => {
      const row = allData.find(x => x.id == rowId)
      if (row && row.ship_to_country == "AU") {
        const found = ausStates.find(x => x == val)
        if (!found) return `${val} is unavailable.`
      }

      return ""
    },
  },
  carrier_service: {
    renderEditCell: params => {
      return <AutocompleteEditInputCell {...params} options={rootStore.getOptionsByFilter("SERVICE").map(option => ({ label: option.label, id: option.value }))} />
    },
    customValidation: (rowId, val, allData) => {
      const found = rootStore.getOptionsByFilter("SERVICE").find(x => x.value == val)
      if (!found) return `${val} is unavailable.`

      return ""
    },
  },
  item: {
    renderEditCell: params => {
      return <AutocompleteEditInputCell {...params} options={GlobalData.ProductListSelection} />
    },
    customValidation: (rowId, productCode, allData) => {
      productCode = (productCode || "").toUpperCase()
      const found = GlobalData.ProductListSelection.find(x => x.id.toUpperCase() == productCode)
      if (!found) return "Invalid Product Code"

      const curRow = allData.find(x => x["id"] == rowId)
      const curOrderId = curRow ? curRow["erp_order"] : ""
      const existed = allData.find(x => x["item"].toUpperCase() == productCode && rowId != x.id && curOrderId == x["erp_order"])
      if (existed) return `${productCode} already exsist`

      return ""
    },
  },
  erp_order: {
    customValidation: (rowId, val, allData) => {
      if (val?.indexOf(" ") >= 0) return "Order ID should not include space."

      return ""
    },
  },
  warehouse: {
    renderEditCell: params => {
      return <AutocompleteEditInputCell {...params} options={rootStore.getOptionsByFilter("WAREHOUSE").map(option => ({ label: option.label, id: option.value }))} />
    },
    customValidation: (rowId, val, allData) => {
      const found = rootStore.getOptionsByFilter("WAREHOUSE").find(x => x.value == val)
      if (!found) return `${val} is unavailable.`

      return ""
    },
  },
  ship_to_country: {
    renderEditCell: params => {
      return <AutocompleteEditInputCell {...params} options={rootStore.getOptionsByFilter("COUNTRY").map(option => ({ label: option.label, id: option.value }))} />
    },
    customValidation: (rowId, val, allData) => {
      const found = rootStore.getOptionsByFilter("COUNTRY").find(x => x.value == val)
      if (!found) return `${val} is unavailable.`

      return ""
    },
  },
  scheduled_ship_date: {
    valueGetter: params => {
      if (!params) {
        return null
      }

      if (params.value) {
        return typeof params.value == "string" ? parseDate(params.value) : params.value
      }

      return typeof params == "string" ? parseDate(params) : params
    },
    renderEditCell: params => {
      return <DateTimeEditInputCell {...params} />
    },
  },
  country_of_origin: {
    renderEditCell: params => {
      return <AutocompleteEditInputCell {...params} options={rootStore.getOptionsByFilter("COUNTRY").map(option => ({ label: option.label, id: option.value }))} />
    },
    customValidation: (rowId, val, allData) => {
      const found = rootStore.getOptionsByFilter("COUNTRY").find(x => x.value == val)
      if (!found) return `${val} is unavailable.`

      return ""
    },
  },
  delivery_code: {
    customValidation: (rowId, val, allData) => {
      const found = deliveryCodes.find(x => x == val)
      if (!found) return `${val} is unavailable.`

      return ""
    },
  },
  sales_channel: {
    renderEditCell: params => {
      return (
        <RadioGroupEditInputCell
          {...params}
          options={[
            { value: "", label: "None" },
            { value: "B2B", label: "B2B" },
            { value: "B2C", label: "B2C" },
          ]}
        />
      )
    },
  },
  incoterm: {
    renderEditCell: params => {
      return <AutocompleteEditInputCell {...params} options={rootStore.getOptionsByFilter("INCOTERMS").map(option => ({ label: option.label, id: option.value }))} />
    },
    customValidation: (rowId, val, allData) => {
      const found = rootStore.getOptionsByFilter("INCOTERMS").find(x => x.value == val)
      if (!found) return `${val} is unavailable.`

      return ""
    },
  },
}

export const columnsUpload = [
  {
    field: "ship_to_name",
    headerName: "Delivery Name",
    width: 180,
    editable: true,
    maxLength: 40,
    required: true,
  },
  {
    field: "ship_to_address1",
    headerName: "Delivery Address 1",
    width: 200,
    editable: true,
    maxLength: 40,
    required: true,
  },
  {
    field: "ship_to_address2",
    headerName: "Delivery Address 2",
    width: 150,
    editable: true,
    maxLength: 40,
  },
  {
    field: "ship_to_city",
    headerName: "Delivery Suburb",
    editable: true,
    maxLength: 40,
    required: true,
    width: 120,
  },
  {
    field: "ship_to_state",
    headerName: "Delivery State",
    editable: true,
    width: 140,
    maxLength: 25,
    required: true,
  },
  {
    field: "ship_to_postal_code",
    headerName: "Delivery Post Code",
    editable: true,
    maxLength: 10,
    required: true,
    width: 150,
  },
  {
    field: "quantity",
    headerName: "Quantity",
    type: "number",
    editable: true,
    maxLength: 4,
    required: true,
  },
  {
    field: "carrier_service",
    headerName: "Service",
    editable: true,
    maxLength: 25,
    width: 150,
  },
  {
    field: "item",
    headerName: "Product Code",
    editable: true,
    maxLength: 25,
    required: true,
    width: 180,
    shouldValidateAllRowsAfterChanging: true,
  },
  {
    field: "erp_order",
    headerName: "Order Id",
    mappings: ["Order ID"],
    width: 150,
    editable: true,
    maxLength: 21,
    required: true,
  },
  {
    field: "ship_to_email_address",
    headerName: "Delivery Email Address",
    editable: true,
    maxLength: 50,
    width: 150,
  },
  {
    field: "ship_to_phone_num",
    headerName: "Delivery Phone Number",
    editable: true,
    maxLength: 40,
    width: 150,
  },
  {
    field: "special_details_2",
    headerName: "Special Details 2",
    editable: true,
    maxLength: 50,
    width: 150,
  },
  {
    field: "special_details_3",
    headerName: "Special Details 3",
    editable: true,
    maxLength: 40,
    width: 150,
  },
  {
    field: "warehouse",
    headerName: "Origin Warehouse",
    editable: true,
    maxLength: 10,
    required: true,
    width: 150,
  },
  {
    field: "ship_to_country",
    headerName: "Destination Country",
    editable: true,
    required: true,
    width: 150,
  },
  {
    field: "scheduled_ship_date",
    headerName: "Scheduled Ship Date",
    width: 150,
    editable: true,
  },
  {
    field: "customer_po",
    headerName: "Order Line Purchase Order Reference",
    editable: true,
    maxLength: 25,
    width: 150,
  },
  {
    field: "net_price",
    headerName: "Item List Price",
    type: "number",
    editable: true,
    maxLength: 19,
    width: 150,
  },
  {
    field: "country_of_origin",
    headerName: "Country Of Origin",
    mappings: ["Country of Origin"],
    editable: true,
    maxLength: 2,
    width: 150,
  },
  {
    field: "delivery_code",
    headerName: "Authority To Leave",
    mappings: ["Delivery Code"],
    editable: true,
    maxLength: 6,
    width: 150,
    type: "singleSelect",
    valueOptions: deliveryCodes,
  },
  {
    field: "sales_channel",
    headerName: "Sales Channel",
    editable: true,
    width: 250,
  },
  {
    field: "reserved_8",
    headerName: "Gift Card Greeting",
    editable: true,
    maxLength: 50,
    width: 120,
  },
  {
    field: "reserved_9",
    headerName: "Gift Card Name",
    editable: true,
    maxLength: 50,
    width: 120,
  },
  {
    field: "mark_for_address3",
    headerName: "Gift Card Message",
    editable: true,
    maxLength: 500,
    width: 150,
  },
  {
    field: "brand_code",
    headerName: "Brand Code",
    editable: true,
    maxLength: 10,
    width: 150,
  },
  {
    field: "reserved_1",
    headerName: "Reserved 1",
    editable: true,
    maxLength: 25,
    width: 150,
  },
  {
    field: "Reserved 2",
    headerName: "Reserved 2",
    editable: true,
    maxLength: 25,
    width: 150,
  },
  {
    field: "ship_to",
    headerName: "Company Name",
    editable: true,
    maxLength: 25,
    width: 150,
  },
  {
    field: "reserved_4",
    headerName: "Reserved 4",
    editable: true,
    maxLength: 50,
    width: 150,
  },
  {
    field: "Internal Use Only",
    headerName: "Original Order ID",
    mappings: ["Internal Use Only"],
    editable: true,
    width: 150,
  },
  {
    field: "reserved_6",
    headerName: "Reserved 6",
    editable: true,
    maxLength: 50,
    width: 150,
  },
  {
    field: "customer_name",
    headerName: "Customer Name",
    editable: true,
    maxLength: 40,
    width: 150,
  },
  {
    field: "customer_address1",
    headerName: "Customer Address 1",
    editable: true,
    maxLength: 40,
    width: 150,
  },
  {
    field: "customer_address2",
    headerName: "Customer Address 2",
    editable: true,
    maxLength: 40,
    width: 150,
  },
  {
    field: "customer_city",
    headerName: "Customer Suburb",
    editable: true,
    maxLength: 40,
    width: 150,
  },
  {
    field: "customer_state",
    headerName: "Customer State",
    editable: true,
    maxLength: 25,
    width: 150,
  },
  {
    field: "customer_postal_code",
    headerName: "Customer Post Code",
    editable: true,
    maxLength: 10,
    width: 150,
  },
  {
    field: "customer_code",
    headerName: "Customer Code",
    editable: true,
    maxLength: 4,
    width: 150,
  },
  {
    field: "ship_to_code",
    headerName: "Ship to Code",
    editable: true,
    maxLength: 4,
    width: 150,
  },
  {
    field: "default_invoice_currency",
    headerName: "Default Invoice Currency",
    editable: true,
    maxLength: 10,
    width: 150,
  },
  {
    field: "incoterm",
    headerName: "Incoterm",
    editable: true,
    maxLength: 3,
    width: 150,
  },
  {
    field: "delivery_instructions",
    headerName: "Delivery Instructions",
    editable: true,
    maxLength: 25,
    width: 150,
  },
  {
    field: "shipping_method",
    headerName: "Shipping Method",
    editable: true,
    maxLength: 25,
    width: 150,
  },
]

export const reorderedKeys = [
  "shipping_method",
  "delivery_instructions",
  "brand_code",
  "carrier",
  "carrier_service",
  "customer_address1",
  "customer_address2",
  "customer_code",
  "reserved_6",
  "customer_city",
  "customer_name",
  "reserved_4",
  "customer_postal_code",
  "customer_state",
  "delivery_code",
  "sales_channel",
  "default_invoice_currency",
  "ship_to",
  "ship_to_address1",
  "ship_to_address2",
  "ship_to_code",
  "special_details_3",
  "ship_to_city",
  "ship_to_country",
  "ship_to_email_address",
  "special_details_2",
  "ship_to_name",
  "ship_to_phone_num",
  "ship_to_postal_code",
  "ship_to_state",
  "erp_order",
  "incoterm",
  "scheduled_ship_date",
  "warehouse",
  "details",
]

export const detailsKeys = ["action", "customer_po", "erp_order", "erp_order_line_num", "country_of_origin", "item", "net_price", "quantity", "quantity_um"]
